import {Component, ElementRef, HostListener, inject, input, OnDestroy, OnInit, viewChild, ViewContainerRef, ChangeDetectionStrategy} from "@angular/core"
import {NodeEditorCanvasComponent} from "@app/socket-editor/components/node-editor-canvas/node-editor-canvas.component"
import {NodesService} from "@node-editor/services/nodes.service"
import {DragService} from "@app/node-editor/services/drag.service"
import {NodeEditorTransformService} from "@node-editor/services/node-editor-transform.service"
import {SocketConnectionsService} from "@app/socket-editor/services/socket-connections.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-node-editor",
    templateUrl: "./node-editor.component.html",
    styleUrls: ["./node-editor.component.scss"],
    host: {oncontextmenu: "return false"},
    imports: [NodeEditorCanvasComponent],
})
export class NodeEditorComponent<NodeType extends {id: string; name: string}> implements OnDestroy, OnInit {
    readonly $nodeContainer = viewChild.required("nodeContainer", {read: ViewContainerRef})
    readonly $isDirty = input(false, {alias: "isDirty"})

    protected readonly element = inject(ElementRef)
    private readonly nodes = inject(NodesService<NodeType>)
    private readonly connections = inject(SocketConnectionsService)
    private readonly drag = inject(DragService)
    private readonly transformService = inject(NodeEditorTransformService)

    @HostListener("mousedown", ["$event"])
    onMouseDown(_event: MouseEvent) {
        this.nodes.setSelected([])
    }

    @HostListener("mousemove", ["$event"]) onMouseMove(event: MouseEvent) {
        this.drag.continueDraggingNode(event, this.transformService.transform?.scale ?? 1)
    }

    @HostListener("mouseup", ["$event"]) onMouseUp(event: MouseEvent) {
        this.drag.stopDraggingNode(event)
    }

    @HostListener("click", ["$event"]) onClick(_event: MouseEvent) {
        this.nodes.deselectAll()
    }

    @HostListener("document:keydown", ["$event"]) onKeyDown(event: KeyboardEvent) {
        const target = event.target
        const isEditable =
            target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || (target instanceof HTMLElement && target.isContentEditable)
        if ((event.code === "Delete" || event.code === "Backspace") && this.nodes.selectedNodeIds.length && !isEditable) {
            event.stopPropagation()
            this.nodes.remove(this.nodes.selectedNodeIds)
            this.connections.remove(this.connections.forNodes(this.nodes.selectedNodeIds))
        }
    }

    @HostListener("window:beforeunload", ["$event"])
    protected async unloadNotification($event: BeforeUnloadEvent) {
        if (this.$isDirty()) {
            $event.returnValue = true
            $event.preventDefault()
        }
    }

    ngOnInit() {
        this.transformService.register(this.element.nativeElement, this.$nodeContainer().element.nativeElement)
    }

    ngOnDestroy() {
        this.transformService.transform?.unregister()
    }

    // the node editor's center point on the screen
    // (in the coordinates of the scaled and translated node container)
    get viewportCenter() {
        const centerOnScreen = {
            x: this.element.nativeElement.clientWidth / 2,
            y: this.element.nativeElement.clientHeight / 2,
        }
        // also account for CSS transformation
        return this.transformService.transform?.editorToLogicalCoordinates(centerOnScreen) ?? centerOnScreen
    }

    get viewportParameters() {
        return {
            center: {
                x: this.viewportCenter.x,
                y: this.viewportCenter.y,
            },
            scale: this.transformService.transform?.scale ?? 1,
        }
    }
}
